import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';

import {
    ACESFilmicToneMapping,
    Color,
    DirectionalLight,
    Group,
    HemisphereLight,
    LineSegments,
    Mesh,
    Object3D,
    PCFShadowMap,
    PerspectiveCamera,
    SRGBColorSpace,
    Scene,
    Spherical,
    Vector3,
    WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { readSceneBackground } from './scene-background';

/** Sky / ground colours of the hemisphere light: a cool top and a warm bounce from the floor. */
// native-ok: WebGL takes a colour value, not a CSS variable
const HEMISPHERE_SKY_COLOR: number = 0xf2f4ff;
// native-ok: WebGL takes a colour value, not a CSS variable
const HEMISPHERE_GROUND_COLOR: number = 0x5a4632;
const HEMISPHERE_INTENSITY: number = 1.0;
const DIRECTIONAL_INTENSITY: number = 1.2;

/**
 * `instanceof` у обобщённых классов three сужает к `Group<any>`/`Mesh<any, any, any>`, и
 * дальше по коду едет `any`. Признаки, которые three проставляет сам, дают тот же ответ,
 * но сужают к типу с параметрами по умолчанию — то есть с типизированными `geometry`
 * и `children`.
 */
export function isGroup(object: Object3D): object is Group {
    return (object as { isGroup?: boolean }).isGroup === true;
}

export function isMesh(object: Object3D): object is Mesh {
    return (object as { isMesh?: boolean }).isMesh === true;
}

/** Edge lines (`LineSegments`) hang off meshes as children and own their geometry. */
export function isLineSegments(object: Object3D): object is LineSegments {
    return (object as { isLineSegments?: boolean }).isLineSegments === true;
}

@Injectable()
export class SceneService implements OnDestroy {
    readonly #ngZone: NgZone = inject(NgZone);

    // Поля пусты до успешного init(): создание WebGLRenderer падает там, где контекста
    // нет (SSR, отключённый WebGL), и init() выходит раньше, чем заведутся рендерер и
    // управление камерой. Тип это и говорит — проверки ниже не перестраховка, а разбор
    // единственного реального состояния «сцена ещё не поднята».
    public scene: Scene | null = null;
    public camera: PerspectiveCamera | null = null;
    public renderer: WebGLRenderer | null = null;
    public controls: OrbitControls | null = null;

    #animationId: number = 0;
    #needsRender: boolean = true;
    #stairGroup: Group = new Group();
    #roomGroup: Group = new Group();
    #directionalLight: DirectionalLight | null = null;

    public init(canvas: HTMLCanvasElement, width: number, height: number): void {
        this.scene = new Scene();
        // The canvas is a light stage of its own: the variable --sc-color-scene-ground. Wood, the
        // slab and the dark edge lines read against it; the floor grid lies on it.
        this.scene.background = new Color(readSceneBackground(canvas));
        this.#addLights(this.scene);

        this.camera = new PerspectiveCamera(50, width / height, 1, 50000);
        this.#positionCamera(width, height);

        try {
            this.renderer = new WebGLRenderer({ canvas, antialias: true });
        } catch (e: unknown) {
            // eslint-disable-next-line no-console
            console.error('WebGL is not available:', e);
            return;
        }

        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio, 2));
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = PCFShadowMap;
        this.renderer.toneMapping = ACESFilmicToneMapping;
        // Exposure > 1 keeps wood mid-tones near the pre-tonemapping brightness
        // the original pre-texture UI had — ACES compresses mid-tones by default.
        this.renderer.toneMappingExposure = 1.35;
        this.renderer.outputColorSpace = SRGBColorSpace;

        this.controls = new OrbitControls(this.camera, canvas);
        this.controls.target.set(2000, 1200, 2500);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.08;
        this.controls.minDistance = 500;
        this.controls.maxDistance = 15000;
        this.controls.update();
        this.controls.addEventListener('change', () => {
            this.#needsRender = true;
        });

        this.scene.add(this.#roomGroup);
        this.scene.add(this.#stairGroup);

        this.#ngZone.runOutsideAngular(() => {
            this.#animate();
        });
    }

    /** Light preset: a hemisphere fill (sky above, warm floor bounce below) and a shadow-casting sun. */
    #addLights(scene: Scene): void {
        const hemisphere: HemisphereLight = new HemisphereLight(HEMISPHERE_SKY_COLOR, HEMISPHERE_GROUND_COLOR, HEMISPHERE_INTENSITY);
        hemisphere.position.set(0, 10000, 0);
        scene.add(hemisphere);

        const directional: DirectionalLight = new DirectionalLight(0xffffff, DIRECTIONAL_INTENSITY);
        directional.position.set(3000, 5000, 3000);
        directional.castShadow = true;
        directional.shadow.mapSize.set(2048, 2048);
        directional.shadow.camera.near = 100;
        directional.shadow.camera.far = 15000;
        directional.shadow.camera.left = -5000;
        directional.shadow.camera.right = 5000;
        directional.shadow.camera.top = 5000;
        directional.shadow.camera.bottom = -5000;
        scene.add(directional);
        this.#directionalLight = directional;
    }

    public clearStair(): void {
        this.#disposeGroup(this.#stairGroup);
    }

    public addToStair(mesh: Object3D): void {
        this.#stairGroup.add(mesh);
    }

    public setStairPosition(x: number, y: number, z: number): void {
        this.#stairGroup.position.set(x, y, z);
    }

    public clearRoom(): void {
        this.#disposeGroup(this.#roomGroup);
    }

    #disposeGroup(group: Group): void {
        while (group.children.length > 0) {
            const child: Object3D = group.children[0];
            group.remove(child);
            if (isGroup(child)) {
                this.#disposeGroup(child);
            } else if (isMesh(child)) {
                this.#disposeMesh(child);
            } else if (isLineSegments(child)) {
                // Free-standing lines (the floor grid) own their geometry; the material is the builder's.
                child.geometry.dispose();
            } else {
                /* no-op: прочие типы объектов не требуют освобождения ресурсов */
            }
        }
    }

    /** A mesh frees its geometry and the edge lines hanging off it; materials belong to the builders. */
    #disposeMesh(mesh: Mesh): void {
        mesh.geometry.dispose();
        mesh.children.forEach((child: Object3D) => {
            if (isLineSegments(child)) {
                child.geometry.dispose();
            }
        });
    }

    public addToRoom(mesh: Object3D): void {
        this.#roomGroup.add(mesh);
    }

    public resize(width: number, height: number): void {
        if (!this.renderer || !this.camera) {
            return;
        }
        this.camera.aspect = width / height;
        this.#positionCamera(width, height);
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);
        this.#needsRender = true;
    }

    #positionCamera(width: number, height: number): void {
        if (!this.camera) {
            return;
        }
        const target: { x: number; y: number; z: number } = { x: 2000, y: 1200, z: 2500 };
        const aspect: number = width / height;
        const distanceScale: number = aspect < 1 ? 1 / Math.max(aspect, 0.4) : 1;
        this.camera.position.set(-2000 * distanceScale, 4000 * distanceScale, -3000 * distanceScale);
        this.camera.lookAt(target.x, target.y, target.z);
        if (this.controls) {
            this.controls.target.set(target.x, target.y, target.z);
            this.controls.update();
        }
    }

    public requestRender(): void {
        this.#needsRender = true;
    }

    public panCamera(deltaX: number, deltaY: number): void {
        if (!this.camera || !this.controls) {
            return;
        }
        const right: Vector3 = new Vector3();
        const up: Vector3 = new Vector3();
        const forward: Vector3 = new Vector3();
        this.camera.matrixWorld.extractBasis(right, up, forward);
        const pan: Vector3 = new Vector3().addScaledVector(right, deltaX).addScaledVector(up, deltaY);
        this.camera.position.add(pan);
        this.controls.target.add(pan);
        this.controls.update();
        this.#needsRender = true;
    }

    public zoomCamera(delta: number): void {
        if (!this.camera || !this.controls) {
            return;
        }
        const offset: Vector3 = new Vector3().subVectors(this.camera.position, this.controls.target);
        const currentDistance: number = offset.length();
        const newDistance: number = Math.max(this.controls.minDistance, Math.min(this.controls.maxDistance, currentDistance - delta));
        offset.setLength(newDistance);
        this.camera.position.copy(this.controls.target).add(offset);
        this.controls.update();
        this.#needsRender = true;
    }

    public orbitCamera(deltaAzimuth: number, deltaPolar: number): void {
        if (!this.camera || !this.controls) {
            return;
        }
        const offset: Vector3 = new Vector3().subVectors(this.camera.position, this.controls.target);
        const spherical: Spherical = new Spherical().setFromVector3(offset);
        spherical.theta -= deltaAzimuth;
        spherical.phi -= deltaPolar;
        const epsilon: number = 0.01;
        spherical.phi = Math.max(epsilon, Math.min(Math.PI - epsilon, spherical.phi));
        offset.setFromSpherical(spherical);
        this.camera.position.copy(this.controls.target).add(offset);
        this.camera.lookAt(this.controls.target);
        this.controls.update();
        this.#needsRender = true;
    }

    #animate(): void {
        if (!this.renderer || !this.scene || !this.camera || !this.controls) {
            return;
        }
        this.#animationId = requestAnimationFrame(() => {
            this.#animate();
        });
        this.controls.update();
        if (this.#needsRender) {
            this.#needsRender = false;
            this.renderer.render(this.scene, this.camera);
        }
    }

    public ngOnDestroy(): void {
        if (typeof globalThis.cancelAnimationFrame === 'function') {
            cancelAnimationFrame(this.#animationId);
        }
        this.clearStair();
        this.clearRoom();
        // The shadow map render target is owned by the light, not the renderer, so
        // renderer.dispose() leaves it allocated. Free it explicitly.
        this.#directionalLight?.shadow.map?.dispose();
        this.renderer?.dispose();
        this.controls?.dispose();
    }
}
